/**
 * Style parsing utilities for converting inline CSS to React style objects
 */

export interface ReactStyleObject {
  [key: string]: string | number;
}

/**
 * Converts CSS property names to camelCase for React
 */
function toCamelCase(prop: string): string {
  if (!prop) {
    return prop;
  }

  const trimmed = prop.trim();

  // Keep custom properties as-is
  if (trimmed.startsWith('--')) {
    return trimmed;
  }

  // Special case for float
  if (trimmed === 'float') {
    return 'cssFloat';
  }

  // Handle vendor prefixes and hyphens
  return trimmed
    .replace(/^-+/, (m) => m.replace(/-/, '').toUpperCase()) // -webkit -> Webkit
    .replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

/**
 * Processes a character in the CSS declaration parsing state machine
 */
function processChar(
  char: string,
  buf: string,
  inSingleQts: boolean,
  inDoubleQts: boolean,
  parenDepth: number,
): {
  buf: string;
  inSingleQts: boolean;
  inDoubleQts: boolean;
  parenDepth: number;
  shouldSplit: boolean;
} {
  // Helper function to create return object with common pattern
  const makeResult = (
    newBuf: string,
    newInSingleQts: boolean = inSingleQts,
    newInDoubleQts: boolean = inDoubleQts,
    newParenDepth: number = parenDepth,
    shouldSplit: boolean = false,
  ) => ({
    buf: newBuf,
    inSingleQts: newInSingleQts,
    inDoubleQts: newInDoubleQts,
    parenDepth: newParenDepth,
    shouldSplit,
  });

  // Handle single quotes (toggle state, but only if not inside double quotes)
  if (char === "'" && !inDoubleQts) {
    return makeResult(buf + char, !inSingleQts);
  }

  // Handle double quotes (toggle state, but only if not inside single quotes)
  if (char === '"' && !inSingleQts) {
    return makeResult(buf + char, inSingleQts, !inDoubleQts);
  }

  // Handle parentheses and semicolons only when not inside any quotes
  if (!inSingleQts && !inDoubleQts) {
    // Opening parenthesis: increase nesting depth
    if (char === '(') {
      return makeResult(buf + char, inSingleQts, inDoubleQts, parenDepth + 1);
    }

    // Closing parenthesis: decrease nesting depth (but not below 0)
    if (char === ')') {
      return makeResult(
        buf + char,
        inSingleQts,
        inDoubleQts,
        Math.max(0, parenDepth - 1),
      );
    }

    // Semicolon at top level: split the declaration
    if (char === ';' && parenDepth === 0) {
      return makeResult(buf, inSingleQts, inDoubleQts, parenDepth, true);
    }
  }

  // Default case: just add character to buffer (no state changes)
  return makeResult(buf + char);
}

/**
 * Splits CSS declarations, handling nested parentheses and quotes
 */
function splitDeclarations(input: string): string[] {
  const out: string[] = []; // Array to hold individual CSS declarations
  let buf = ''; // Buffer accumulating the current CSS declaration
  let inSingleQts = false;
  let inDoubleQts = false;
  let parenDepth = 0; // Tracks nesting level of parentheses (e.g., in calc(), url())

  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    const result = processChar(char, buf, inSingleQts, inDoubleQts, parenDepth);

    buf = result.buf;
    inSingleQts = result.inSingleQts;
    inDoubleQts = result.inDoubleQts;
    parenDepth = result.parenDepth;

    if (result.shouldSplit) {
      const part = buf.trim();
      if (part) {
        out.push(part);
      }
      buf = '';
    }
  }

  const tail = buf.trim();
  if (tail) {
    out.push(tail);
  }

  return out;
}

function containsDangerousCssUrl(val: string): boolean {
  return /\burl\s*\(\s*(['"])?\s*(?:javascript|vbscript):/i.test(val);
}

/**
 * Parses inline CSS text to a React style object
 */
export function parseInlineStyle(styleText: string): ReactStyleObject {
  const style: ReactStyleObject = {};

  if (!styleText) {
    return style;
  }

  const decls = splitDeclarations(styleText);

  for (const decl of decls) {
    // Split on first colon at top-level
    const idx = decl.indexOf(':');
    if (idx === -1) {
      continue;
    }

    const rawProp = decl.slice(0, idx).trim();
    if (!rawProp) {
      continue;
    }

    const value = decl.slice(idx + 1).trim();
    if (!value) {
      continue;
    }

    // Drop style containing potentially dangerous javascript
    if (containsDangerousCssUrl(value)) {
      continue;
    }

    // Keep '!important' as-is (React will ignore priority but value is preserved)
    const propName = toCamelCase(rawProp);
    style[propName] = value;
  }

  return style;
}
